import React, { createContext, ReactNode, useContext } from 'react';
import { isReady } from 'shared/src/utils/zod/trpc-zod-utils';
import { StrategyApiResponse } from 'shared/src/api/strategy-api-types';
import { StrategyTrpcResponse } from 'shared/src/trpc-types/strategy-trpc-types';
import {
  ExecutionLineItemTrpcResponse,
  TrpcExecutionLineItemListResponse
} from 'shared/src/trpc-types/line-item-trpc-types';
import { TRPCQueryResult } from '@/utils/trpc-client';

type ContextValue = {
  execution: StrategyTrpcResponse;
  lineItems: ExecutionLineItemTrpcResponse[];
};

export const ExecutionContext = createContext<ContextValue | null>(null);

type Props = {
  execution: TRPCQueryResult<StrategyApiResponse>;
  lineItems: TRPCQueryResult<TrpcExecutionLineItemListResponse>;
  children: ReactNode;
};

// handle waiting for data to load before rendering
export function ExecutionContextProvider({ execution, lineItems, children }: Props) {
  if (execution.isError || lineItems.isError) {
    return <div>Error loading execution data.</div>;
  }

  if (!isReady(execution) || !isReady(lineItems)) {
    return <div>Loading...</div>;
  }

  return (
    <ExecutionContext
      value={{
        execution: StrategyTrpcResponse.parse(execution.data),
        lineItems: lineItems.data.data
      }}>
      {children}
    </ExecutionContext>
  );
}

export function useExecutionContext() {
  const value = useContext(ExecutionContext);
  if (!value) {
    throw new Error('useExecutionContext must be used within a ExecutionContextProvider');
  }
  return value;
}
